import { useEffect, useMemo, useState } from "react";
import { Loader2, Search, Users } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const ROOMS = ["Freebies", "Inspire", "Journey", "Shop"];

const ROOM_STYLES = {
  Freebies: "bg-[#A8BFCE]/60 text-ink",
  Inspire: "bg-[#C06A4E]/20 text-ink",
  Journey: "bg-[#7E9070]/25 text-ink",
  Shop: "bg-[#B96A47]/20 text-ink",
};

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : "—";

export const SubscribersPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");

  useEffect(() => {
    api
      .get("/admin/subscribers")
      .then(({ data }) => setItems(data.items))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, []);

  const counts = useMemo(() => {
    const c = { total: items.length };
    ROOMS.forEach((r) => {
      c[r] = items.filter((s) => (s.interests || []).includes(r)).length;
    });
    return c;
  }, [items]);

  const visible = useMemo(() => {
    let list = items;
    if (filter !== "all") list = list.filter((s) => (s.interests || []).includes(filter));
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((s) => s.email.includes(q));
    const sorted = [...list];
    if (sort === "newest") sorted.sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
    if (sort === "oldest") sorted.sort((a, b) => (a.updated_at || "").localeCompare(b.updated_at || ""));
    if (sort === "email") sorted.sort((a, b) => a.email.localeCompare(b.email));
    return sorted;
  }, [items, filter, search, sort]);

  return (
    <div data-testid="subscribers-panel" className="mx-auto w-full max-w-5xl px-4 py-6 sm:px-6 sm:py-8">
      {/* Counts */}
      <div className="flex flex-wrap gap-2" data-testid="subscriber-counts">
        <span className="rounded-full bg-white/70 px-4 py-2 text-xs font-semibold text-ink" data-testid="subscriber-count-total">
          <Users className="mr-1.5 inline h-3.5 w-3.5" />
          Total: {counts.total}
        </span>
        {ROOMS.map((r) => (
          <span key={r} className="rounded-full bg-white/70 px-4 py-2 text-xs font-medium text-ink/70" data-testid={`subscriber-count-${r.toLowerCase()}`}>
            {r}: {counts[r]}
          </span>
        ))}
      </div>

      {/* Filters */}
      <div className="mt-5 flex flex-wrap gap-2">
        {["all", ...ROOMS].map((f) => (
          <button
            key={f}
            type="button"
            data-testid={`sub-filter-${String(f).toLowerCase()}`}
            onClick={() => setFilter(f)}
            className={`min-h-[40px] rounded-full px-4 text-[12px] font-semibold transition-colors ${
              filter === f ? "bg-[#A8BFCE] text-ink" : "bg-white/70 text-ink/60 hover:text-ink"
            }`}
          >
            {f === "all" ? "All" : f}
          </button>
        ))}
      </div>

      {/* Search + sort */}
      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/35" />
          <input
            type="search"
            data-testid="subscriber-search"
            placeholder="Search by email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-12 w-full rounded-full border border-ink/15 bg-white pl-11 pr-4 text-[15px] text-ink focus:border-ink/40 focus:outline-none"
          />
        </div>
        <select
          data-testid="subscriber-sort"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="h-12 rounded-full border border-ink/15 bg-white px-4 text-[13px] font-medium text-ink/80 focus:border-ink/40 focus:outline-none"
        >
          <option value="newest">Newest activity</option>
          <option value="oldest">Oldest activity</option>
          <option value="email">Email A–Z</option>
        </select>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-20" data-testid="subscribers-loading">
          <Loader2 className="h-6 w-6 animate-spin text-ink/40" />
        </div>
      ) : visible.length === 0 ? (
        <p className="py-20 text-center text-sm text-ink/50" data-testid="subscribers-empty">
          {items.length === 0
            ? "No signups yet — they'll appear here the moment someone joins."
            : "No subscribers match this view."}
        </p>
      ) : (
        <ul className="mt-5 space-y-3" data-testid="subscriber-list">
          {visible.map((s) => (
            <li key={s.email} data-testid={`subscriber-row-${s.email}`} className="rounded-3xl bg-white/70 p-4 sm:p-5">
              <p className="break-all font-serif text-lg font-medium leading-snug text-ink" data-testid={`subscriber-email-${s.email}`}>
                {s.email}
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(s.interests || []).map((i) => (
                  <span key={i} className={`rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] ${ROOM_STYLES[i] || "bg-ink/10 text-ink"}`}>
                    {i}
                  </span>
                ))}
              </div>
              <p className="mt-2.5 text-xs leading-relaxed text-ink/55">
                Joined {fmtDate(s.created_at)} · {s.source}
                {s.updated_at && s.updated_at !== s.created_at && (
                  <> · updated {fmtDate(s.updated_at)}</>
                )}
                {s.signup_count > 1 && <> · {s.signup_count} signups</>}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
